<template>
  <div class="sw-schedule" :style="scheduleVars" :class="{ 'is-loading': loading, 'is-readonly': readonly }">
    <div class="sw-schedule__wrap">
      <div class="sw-schedule__daybar">{{ dateLabel }}</div>

      <div class="sw-schedule__head">
        <div class="sw-schedule__cell sw-schedule__cell--name sw-schedule__cell--head">Manager</div>
        <div class="sw-schedule__timeline sw-schedule__timeline--head">
          <div
            v-for="(hour, idx) in hoursOrder"
            :key="`h-${date}-${idx}`"
            class="sw-schedule__hour"
            :data-label="`${hour.toString().padStart(2, '0')}:00`"
            :data-count="hourCountAtIndex(idx)"
          ></div>
        </div>
        <div class="sw-schedule__cell sw-schedule__cell--total sw-schedule__cell--head">Total Hours</div>
        <div class="sw-schedule__cell sw-schedule__cell--off sw-schedule__cell--head">Day Off</div>
      </div>

      <div v-for="person in people" :key="`row-${date}-${person.id}-${(rowVersions[person.id] || 0)}`" class="sw-schedule__row">
        <div class="sw-schedule__cell sw-schedule__cell--name" :class="nameColorClass(person.id)">
          {{ person.first_name }} {{ person.last_name.charAt(0) }}.
        </div>
        <div
          class="sw-schedule__timeline"
          :class="timelineClass(person.id)"
          :style="{ '--sel-color': rowSelectionColor(person) }"
          @pointerdown="onRowDown(person, $event)"
          @pointermove="onRowMove($event)"
          @pointerup="onRowUp($event)"
          @pointerleave="onRowUp($event)"
        >
          <div v-if="loading" class="sk sk-timeline"></div>

          <div
            v-if="isDayOff(person.id) || isLeave(person.id)"
            class="sw-schedule__overlay"
            :class="overlayClass(person.id)"
          >
            {{ isLeave(person.id) ? leaveUntilLabel(person.id) : 'DAY OFF' }}
          </div>

          <div
            v-for="(_, slotIndex) in slotsCount"
            :key="`s-${date}-${person.id}-${slotIndex}`"
            class="sw-schedule__slot"
            :class="{ 'sw-schedule__slot--sel': isSlotSelected(person, slotIndex) }"
          ></div>
        </div>

        <div
          class="sw-schedule__cell sw-schedule__cell--total"
          :class="[isDayOff(person.id) ? '' : totalClass(person)]"
        >
          <span v-if="loading" class="sk sk-text" style="width:32px"></span>
          <template v-else-if="!isLeave(person.id)">
            {{ isDayOff(person.id) ? '-' : rowTotalHours(person).toFixed(1) }}
          </template>
        </div>

        <div class="sw-schedule__cell sw-schedule__cell--off">
          <span v-if="loading" class="sk sk-checkbox"></span>
          <label v-else-if="!isLeave(person.id)" class="sw-schedule__off-toggle">
            <input
              class="sw-schedule__off-input"
              type="checkbox"
              :checked="isDayOff(person.id)"
              :disabled="readonly || (myUserId && person.id !== myUserId) || isLeave(person.id)"
              @change="onToggleDayOffId(person.id, $event.target.checked)"
            >
            <span class="sw-schedule__off-box"></span>
          </label>
        </div>
      </div>
      <template v-if="loading && (!people || people.length === 0)">
        <div
          v-for="n in skeletonRows"
          :key="`sk-${date}-${n}`"
          class="sw-schedule__row"
        >
          <div class="sw-schedule__cell sw-schedule__cell--name">
            <span class="sk sk-text" style="width:70%"></span>
          </div>

          <div class="sw-schedule__timeline">
            <div class="sk sk-timeline"></div>
          </div>

          <div class="sw-schedule__cell sw-schedule__cell--total">
            <span class="sk sk-text" style="width:32px"></span>
          </div>

          <div class="sw-schedule__cell sw-schedule__cell--off">
            <span class="sk sk-checkbox"></span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'

const LOW_HOURS = 4
const HIGH_HOURS = 8

export default {
  name: 'TheSalesWorkSchedule',
  props: {
    start: { type: String, default: '05:00', validator: v => /^(\d{1,2}):([0-5]\d)$/.test(v) },
    slotsPerHour: { type: Number, default: 2, validator: v => [1, 2].includes(v) },
    date: { type: String, required: true },
    dateLabel: { type: String, required: true },
    week: { type: Object, required: true },
    people: { type: Array, default: () => [] },
    bitsByUser: { type: Object, default: () => ({}) },
    dayOffByUser: { type: Object, default: () => ({}) },
    leavesByUser: { type: Object, default: () => ({}) },
    hourlySummary: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    myUserId: { type: Number, required: false },
    rowVersions: { type: Object, default: () => ({}) },
    skeletonRows: { type: Number, default: 1 }
  },
  data() {
    return {
      rowsById: {},
      dayOffById: {},
      drag: null,
      lastClickIdx: null,
      lastClickRow: null
    }
  },
  created() {
    this.rebuildRows()
    this.applyDayOffFromProp()
    this.applyLeavesFromProp()
  },
  watch: {
    people() {
      this.rebuildRows()
    },
    bitsByUser() {
      this.applyBitsFromProp()
    },
    dayOffByUser() {
      this.applyDayOffFromProp()
    },
    leavesByUser() {
      this.applyLeavesFromProp()
    },
    slotsPerHour() {
      this.rebuildRows()
      if (this.lastClickIdx !== null && this.lastClickIdx >= this.slotsCount) this.lastClickIdx = null
    }
  },
  computed: {
    slotsCount() {
      return 24 * this.slotsPerHour
    },
    startHourNum() {
      const hour = Number(this.start.split(':')[0])
      return ((hour % 24) + 24) % 24
    },
    hoursOrder() {
      const order = []
      for (let i = 0; i < 24; i++) {
        order.push((this.startHourNum + i) % 24)
      }
      return order
    },
    scheduleVars() {
      return {
        '--slots-per-hour': this.slotsPerHour,
        '--c-minor': this.slotsPerHour === 2 ? '#f2f2f2' : 'transparent'
      }
    },
    dragRange() {
      if (!this.drag) return null
      return this.drag.from <= this.drag.to ? [this.drag.from, this.drag.to] : [this.drag.to, this.drag.from]
    }
  },
  methods: {
    rebuildRows() {
      const expectedLength = this.slotsCount
      const ids = new Set(this.people.map(person => person.id))

      for (const person of this.people) {
        if (!this.rowsById[person.id]) this.$set(this.rowsById, person.id, Array(expectedLength).fill(0))
        if (!(person.id in this.dayOffById)) this.$set(this.dayOffById, person.id, false)

        const current = this.rowsById[person.id]
        if (current.length !== expectedLength) {
          const adjusted = current.slice(0, expectedLength)
          while (adjusted.length < expectedLength) adjusted.push(0)
          this.$set(this.rowsById, person.id, adjusted)
        }
      }

      for (const key of Object.keys(this.rowsById)) {
        if (!ids.has(Number(key))) this.$delete(this.rowsById, key)
      }
      for (const key of Object.keys(this.dayOffById)) {
        if (!ids.has(Number(key))) this.$delete(this.dayOffById, key)
      }

      this.applyBitsFromProp()
    },
    applyBitsFromProp() {
      const expectedLength = this.slotsCount

      for (const person of this.people) {
        const bits48 = String(this.bitsByUser[person.id] || '').replace(/[^01]/g, '').padEnd(48, '0').slice(0, 48)
        if (!bits48) continue

        const row = this.rowsById[person.id] || Array(expectedLength).fill(0)

        if (this.slotsPerHour === 2) {
          for (let i = 0; i < 48; i++) {
            row[i] = bits48[i] === '1' ? 1 : 0
          }
        } else {
          for (let h = 0; h < 24; h++) {
            row[h] = (bits48[h * 2] === '1') || (bits48[(h * 2) + 1] === '1') ? 1 : 0
          }
        }

        this.$set(this.rowsById, person.id, row)
      }
    },
    applyDayOffFromProp() {
      for (const person of this.people) {
        const off = !!this.dayOffByUser[person.id]

        this.$set(this.dayOffById, person.id, off)
        if (off) this.applyOnRange(this.getSlots(person.id), 0, this.slotsCount - 1, () => 0)
      }
    },

    applyLeavesFromProp() {
      for (const person of this.people) {
        if (this.isLeave(person.id)) this.applyOnRange(this.getSlots(person.id), 0, this.slotsCount - 1, () => 0)
      }
    },
    getSlots(personId) {
      return this.rowsById[personId] || []
    },
    isDayOff(personId) {
      return !!this.dayOffById[personId]
    },
    isLeave(personId) {
      return !!this.leavesByUser[personId]
    },
    isLocked(personId) {
      if (this.readonly) return true
      if (this.isDayOff(personId) || this.isLeave(personId)) return true
      return !!(this.myUserId && personId !== this.myUserId)
    },
    leaveInfo(personId) {
      const leave = this.leavesByUser[personId]
      return {
        name: leave.type.name,
        code: leave.type.code,
        toDate: leave.to_date
      }
    },
    leaveUntilLabel(personId) {
      const { name, toDate } = this.leaveInfo(personId)
      const pretty = format(parseISO(toDate), 'dd LLL yyyy')
      return `${name} UNTIL ${pretty}`
    },
    timelineClass(personId) {
      const classes = { 'is-locked': this.isLocked(personId) }
      if (this.isDayOff(personId)) {
        classes['is-dayoff'] = true
        return classes
      }
      if (this.isLeave(personId)) {
        const { code } = this.leaveInfo(personId)
        classes['is-leave'] = true
        if (code === 'paid_leave') classes['is-leave--paid'] = true
        else if (code === 'sick_leave') classes['is-leave--sick'] = true
        else if (code === 'unpaid_leave') classes['is-leave--unpaid'] = true
        return classes
      }
      return classes
    },
    overlayClass(personId) {
      if (this.isDayOff(personId)) return ['overlay--dayoff']
      if (this.isLeave(personId)) {
        const { code } = this.leaveInfo(personId)
        return ['overlay--leave', code === 'paid_leave' ? 'overlay--leave-paid' : code === 'sick_leave' ? 'overlay--leave-sick' : code === 'unpaid_leave' ? 'overlay--leave-unpaid' : '']
      }
      return []
    },
    nameColorClass(personId) {
      if (this.isDayOff(personId)) return 'name--dayoff'
      if (this.isLeave(personId)) {
        const { code } = this.leaveInfo(personId)
        if (code === 'paid_leave') return 'name--leave-paid'
        if (code === 'sick_leave') return 'name--leave-sick'
        if (code === 'unpaid_leave') return 'name--leave-unpaid'
      }
      return ''
    },
    applyOnRange(arr, from, to, fn) {
      const start = Math.max(0, Math.min(from, to))
      const end = Math.min(arr.length - 1, Math.max(from, to))
      for (let i = start; i <= end; i++) {
        arr[i] = fn(arr[i], i)
      }
    },
    onRowDown(person, ev) {
      if (this.loading || this.readonly) return
      if (this.isDayOff(person.id) || this.isLeave(person.id)) return
      if (this.myUserId && person.id !== this.myUserId) return
      if (ev.pointerType === 'mouse' && (ev.buttons & 1) === 0) return

      ev.currentTarget.setPointerCapture(ev.pointerId)
      const rect = ev.currentTarget.getBoundingClientRect()
      const slotWidth = rect.width / this.slotsCount
      const slotIndex = this.slotIndexFromX(rect.left, slotWidth, ev.clientX)

      const withShift = this.lastClickRow === person.id && ev.shiftKey && this.lastClickIdx !== null
      const baseDrag = { person, from: slotIndex, to: slotIndex, mode: 'xor', geom: { left: rect.left, slotWidth } }
      this.drag = withShift ? { ...baseDrag, from: this.lastClickIdx, mode: 'set' } : baseDrag

      this.lastClickIdx = slotIndex
      this.lastClickRow = person.id
    },
    onRowMove(ev) {
      if (!this.drag) return

      const { left, slotWidth } = this.drag.geom
      this.drag.to = this.slotIndexFromX(left, slotWidth, ev.clientX)
    },
    onRowUp() {
      if (!this.drag) return

      const { person, mode } = this.drag
      const range = this.dragRange
      if (!range) {
        this.drag = null
        return
      }

      const [from, to] = range
      const row = this.getSlots(person.id)

      if (mode === 'set') {
        for (let i = from; i <= to; i++) {
          this.$set(row, i, 1)
        }
      } else {
        for (let i = from; i <= to; i++) {
          this.$set(row, i, row[i] ? 0 : 1)
        }
      }

      this.$emit('change', { personId: person.id, bits: this.bitsFromSlots(row), date: this.date })
      this.drag = null
    },
    onToggleDayOffId(personId, value) {
      if (this.readonly || (this.myUserId && personId !== this.myUserId) || this.isLeave(personId)) return
      this.$set(this.dayOffById, personId, !!value)
      if (value) this.applyOnRange(this.getSlots(personId), 0, this.slotsCount - 1, () => 0)
      this.$emit('dayoff-change', { personId, dayOff: !!value, date: this.date })
    },
    slotIndexFromX(left, slotWidth, clientX) {
      const raw = Math.floor((clientX - left) / slotWidth)
      return Math.max(0, Math.min(raw, this.slotsCount - 1))
    },
    isSlotSelected(person, slotIndex) {
      const row = this.getSlots(person.id)
      const selected = row[slotIndex] === 1

      if (!this.drag || this.drag.person !== person) return selected
      const range = this.dragRange
      if (!range) return selected

      const [from, to] = range
      if (slotIndex < from || slotIndex > to) return selected
      return this.drag.mode === 'set' ? true : !selected
    },
    rowSelectedCount(person) {
      const row = this.getSlots(person.id)
      const base = row.reduce((sum, v) => sum + (v ? 1 : 0), 0)

      if (!this.drag || this.drag.person !== person) return base

      const [from, to] = this.dragRange
      let alreadySelectedInRange = 0
      for (let i = from; i <= to; i++) {
        if (row[i]) alreadySelectedInRange++
      }

      const rangeLen = to - from + 1
      if (this.drag.mode === 'set') return base + (rangeLen - alreadySelectedInRange)

      const delta = rangeLen - (2 * alreadySelectedInRange)
      return base + delta
    },
    rowTotalHours(person) {
      return this.rowSelectedCount(person) / this.slotsPerHour
    },
    rowSelectionColor(person) {
      const hours = this.rowTotalHours(person)
      if (hours <= LOW_HOURS) return 'var(--sel-red)'
      if (hours > HIGH_HOURS) return 'var(--sel-orange)'
      return 'var(--sel-green)'
    },

    totalClass(person) {
      const hours = this.rowTotalHours(person)
      if (hours < LOW_HOURS) return 'sw-schedule__cell--total_low'
      if (hours > HIGH_HOURS) return 'sw-schedule__cell--total_over'
      return 'sw-schedule__cell--total_ok'
    },
    bitsFromSlots(row) {
      if (this.slotsPerHour === 2) {
        return row.map(v => (v ? '1' : '0')).join('').padEnd(48, '0').slice(0, 48)
      }
      const doubled = []
      for (let h = 0; h < 24; h++) {
        const bit = row[h] ? '1' : '0'
        doubled.push(bit, bit)
      }
      return doubled.join('')
    },
    hourCountAtIndex(index) {
      const arr = this.hourlySummary
      if (!Array.isArray(arr) || arr.length !== 24) return 0
      const i = Number(index)
      if (!Number.isInteger(i) || i < 0 || i >= 24) return 0
      return Number(arr[i]) || 0
    },
  }
}
</script>

<style scoped>
.sw-schedule {
  --name-col: 140px;
  --hour-w: 40px;
  --hours: 24;
  --slots-per-hour: 2;
  --slot-w: calc(var(--hour-w) / var(--slots-per-hour));
  --slots: calc(var(--hours) * var(--slots-per-hour));
  --timeline-w: calc(var(--hours) * var(--hour-w));
  --total-col: 55px;
  --off-col: 50px;
  --c-border: #e9e9e9;
  --c-header: #fbfbfb;
  --c-hour-muted: #666;
  --c-hour-count: #e85c20;
  --c-minor: #f2f2f2;
  --c-hover: #f5f5f5;
  --sel-red: #ffa591;
  --sel-green: #dbdfbe;
  --sel-orange: #ffd8a8;
  --sel-color: var(--sel-green);
  --total-red: #ef4444;
  --total-orange: #f59e0b;

  position: relative;
  z-index: 0;
  width: calc(var(--name-col) + var(--timeline-w) + var(--total-col) + var(--off-col));
  margin-bottom: 15px;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, .06);
  clip-path: inset(0 round 10px);
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  font-weight: 500;
}

.sw-schedule::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 10;
  border: 1px solid var(--c-border);
  border-radius: 10px;
  pointer-events: none;
}

.sw-schedule__wrap {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: var(--name-col) var(--timeline-w) var(--total-col) var(--off-col);
  grid-auto-rows: auto;
}

.sw-schedule__head, .sw-schedule__row {
  display: contents;
}

.sw-schedule__daybar {
  position: sticky;
  top: 0;
  z-index: 6;
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-bottom: 1px solid var(--c-border);
  background-color: var(--c-header);
  color: #666;
  font-size: 14px;
}

.sw-schedule__cell {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 12px;
  border-bottom: 1px solid var(--c-border);
  background-color: #fff;
  color: #111827;
}

.sw-schedule__cell--name {
  justify-content: flex-start;
  color: #000;
  font-size: 13px;
}

.sw-schedule__cell.is-content-hidden > * {
  visibility: hidden;
  pointer-events: none;
}

.sw-schedule__cell--total, .sw-schedule__cell--off {
  border-left: 1px solid var(--c-border);
  font-size: 12px;
  line-height: 14px;
}

.sw-schedule__cell--total_low {
  color: var(--total-red);
}

.sw-schedule__cell--total_over {
  color: var(--total-orange);
}

.sw-schedule__cell--total_ok {
  color: inherit;
}

.sw-schedule__cell--head {
  position: sticky;
  top: 30px;
  z-index: 6;
  background-color: var(--c-header);
  color: var(--c-hour-muted);
  text-align: center;
}

.sw-schedule__cell--name.sw-schedule__cell--head {
  text-align: left;
  border-top-left-radius: 10px;
}

.sw-schedule__cell--off.sw-schedule__cell--head {
  border-top-right-radius: 10px;
}

.sw-schedule__timeline {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(var(--slots), var(--slot-w));
  height: 30px;
  border-bottom: 1px solid var(--c-border);
  background-color: #fff;
  user-select: none;
  touch-action: none;
}

.sw-schedule__row .sw-schedule__timeline {
  background-image: repeating-linear-gradient(to right, var(--c-minor) 0 1px, transparent 1px var(--slot-w)),
  repeating-linear-gradient(to right, var(--c-border) 0 1px, transparent 1px var(--hour-w));
  background-position: 0 0, 0 0;
  background-repeat: repeat-x, repeat-x;
}

.sw-schedule__timeline.is-dayoff {
  pointer-events: none;
  background-color: #736d79;
  background-image: none;
  opacity: 1;
}

.sw-schedule__timeline.is-leave {
  pointer-events: none;
  background-color: #fff0c2;
  background-image: none;
  opacity: 1;
}

.sw-schedule__timeline.is-locked {
  pointer-events: none;
}

.sw-schedule__timeline.is-locked .sw-schedule__slot {
  cursor: default;
}

.sw-schedule__timeline.is-locked .sw-schedule__slot:hover {
  background-color: transparent;
}

.sw-schedule__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9e9e9e;
  font-size: 14px;
  letter-spacing: .04em;
  pointer-events: none;
}

.sw-schedule__timeline--head {
  position: sticky;
  top: 30px;
  z-index: 6;
  background-color: var(--c-header);
  background-image: repeating-linear-gradient(to right, var(--c-border) 0 1px, transparent 1px var(--hour-w));
  background-position: 0 0;
  background-repeat: repeat-x;
}

.sw-schedule__hour {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
  grid-column: span var(--slots-per-hour);
  color: var(--c-hour-muted);
  font-size: 11px;
  text-align: center;
}

.sw-schedule__hour::before {
  content: attr(data-label);
  line-height: 1;
}

.sw-schedule__hour::after {
  content: attr(data-count);
  line-height: 1;
  font-size: 12px;
  color: var(--c-hour-count);
}

.is-loading .sw-schedule__hour::after {
  content: '';
  display: block;
  width: 20px;
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eee 0%, #f5f5f5 50%, #eee 100%);
  background-size: 200% 100%;
  animation: sk-pulse 1.2s ease-in-out infinite;
  margin: 0 auto;
}

.sw-schedule__slot {
  position: relative;
  z-index: 4;
  display: block;
  height: 30px;
  background-color: transparent;
  cursor: pointer;
}

.sw-schedule__slot:hover {
  background-color: var(--c-hover);
}

.sw-schedule__slot--sel::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: var(--sel-color);
  opacity: .75;
  pointer-events: none;
}

.is-loading .sw-schedule__row .sw-schedule__timeline {
  pointer-events: none;
  filter: blur(1px);
  transition: filter .2s;
}

.is-readonly .sw-schedule__row .sw-schedule__timeline {
  pointer-events: none;
  cursor: default;
}

.is-readonly .sw-schedule__slot {
  cursor: default;
}

.is-readonly .sw-schedule__off-input {
  pointer-events: none;
  opacity: .6;
}

.sw-schedule__off-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.sw-schedule__off-input {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.sw-schedule__off-box {
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1px solid #dadada;
  background-color: #fff;
  cursor: pointer;
}

.sw-schedule__off-input:checked + .sw-schedule__off-box {
  background-color: #90b454;
  border-color: #90b454;
}

.sw-schedule__off-input:disabled + .sw-schedule__off-box {
  opacity: 0.5;
  cursor: default;
}

.sw-schedule__off-box::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 5px;
  width: 9px;
  height: 5px;
  border-left: 2px solid transparent;
  border-bottom: 2px solid transparent;
  transform: rotate(-45deg);
}

.sw-schedule__off-input:checked + .sw-schedule__off-box::after {
  border-left-color: #fff;
  border-bottom-color: #fff;
}

.sw-schedule__row:last-of-type .sw-schedule__cell--name,
.sw-schedule__row:last-of-type .sw-schedule__timeline,
.sw-schedule__row:last-of-type .sw-schedule__cell--total,
.sw-schedule__row:last-of-type .sw-schedule__cell--off {
  border-bottom: 0;
}

.sw-schedule__row:last-of-type .sw-schedule__cell--name {
  border-bottom-left-radius: 10px;
}

.sw-schedule__row:last-of-type .sw-schedule__cell--off {
  border-bottom-right-radius: 10px;
}

.sw-schedule__timeline.is-dayoff {
  pointer-events: none;
  background-color: #736d79;
  background-image: none;
  opacity: 1;
}

.sw-schedule__overlay.overlay--dayoff {
  color: #D4CEDB;
}

.name--dayoff {
  color: #736d79;
}

.sw-schedule__timeline.is-leave {
  pointer-events: none;
  background-image: none;
  opacity: 1;
}

.sw-schedule__timeline.is-leave--paid {
  background-color: #fff0c2;
}

.sw-schedule__timeline.is-leave--sick {
  background-color: #ffd4c2;
}

.sw-schedule__timeline.is-leave--unpaid {
  background-color: #a7d4de;
}

.sw-schedule__overlay.overlay--leave.overlay--leave-paid {
  color: #c6b06b;
}

.sw-schedule__overlay.overlay--leave.overlay--leave-sick {
  color: #c3704d;
}

.sw-schedule__overlay.overlay--leave.overlay--leave-unpaid {
  color: #6c98a1;
}

.name--leave-paid {
  color: #c6b06b;
}

.name--leave-sick {
  color: #c3704d;
}

.name--leave-unpaid {
  color: #6c98a1;
}

@keyframes sk-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes sk-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.sk {
  display: inline-block;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f1f1 0%, #e9e9e9 50%, #f1f1f1 100%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.2s ease-in-out infinite;
}

.sk-text {
  height: 12px;
}

.sk-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  display: inline-block;
}

.sk-timeline {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 0;
  pointer-events: none;
}
</style>
